/**
 * Fetch the scenario's source Inbox conversation for the run-detail
 * "original vs replay" view. The scenario row stores only the customer
 * script — original assistant turns live on the source conversation, which
 * may no longer exist (the FK is SET NULL on delete) or may be behind a
 * disabled inbox feature; callers must treat isError/disabled as "pane
 * unavailable", not a page failure.
 */
import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/services/apiClient'

export type OriginalMessage = {
  id: string
  senderType: 'customer' | 'ai' | 'business' | null
  role: string
  content: string
  timestamp: number
}

export type OriginalConversation = {
  id: string
  customerDisplayName: string | null
  messages: OriginalMessage[]
}

export function useOriginalConversation(
  conversationId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery<OriginalConversation, Error>({
    queryKey: ['replay', 'original-conversation', conversationId ?? ''],
    queryFn: async () => {
      const d = await apiClient.get<Record<string, unknown>>(
        `/api/inbox/conversations/${conversationId}`,
      )
      const rawMessages = (d.messages as Record<string, unknown>[]) ?? []
      return {
        id: d.id as string,
        customerDisplayName: (d.customer_display_name as string) ?? null,
        messages: rawMessages
          .filter((m) => typeof m.content === 'string' && (m.content as string).trim())
          .map((m) => ({
            id: m.id as string,
            senderType: (m.sender_type as OriginalMessage['senderType']) ?? null,
            role: (m.role as string) ?? '',
            content: m.content as string,
            timestamp: (m.timestamp as number) ?? 0,
          })),
      }
    },
    enabled: !!conversationId && enabled,
    retry: false,
    staleTime: 60_000,
  })
}
